/**
 * CMS visibility evaluator (ASA-1570). Framework free and side-effect free apart from a
 * dev-only warning, so spotlight cards, landing sections and any future consumer share one
 * implementation and one set of tests.
 */

import {
    MAX_URL_PATTERN_LENGTH,
    parseVisibility,
    type AgeBucket,
    type Identity,
    type ParsedVisibility,
    type VisibilityRule,
} from './visibilityTypes';

export interface ViewerContext {
    /** Route name matched by `include-pages` / `exclude-pages` (exact match, not sub-pages). */
    page: string;
    /** Full page address matched by the `*-url` regexes. */
    url: string;
    identity: Identity;
    /** null when the viewer has no usable birth date (see resolveAgeBucket). */
    ageBucket: AgeBucket | null;
    /** Two letter code, or null when the profile has no state set. */
    stateCode: string | null;
}

/**
 * Compiled-pattern cache. Editor-typed patterns repeat across many items on a page, and a
 * bad pattern is cached as null so we do not retry compiling it per render.
 */
const patternCache = new Map<string, RegExp | null>();

function compilePattern(pattern: string): RegExp | null {
    if (pattern.length > MAX_URL_PATTERN_LENGTH) return null;
    const cached = patternCache.get(pattern);
    if (cached !== undefined) return cached;
    let compiled: RegExp | null = null;
    try {
        compiled = new RegExp(pattern);
    }
    catch {
        compiled = null;
    }
    patternCache.set(pattern, compiled);
    return compiled;
}

/** @internal Exported for tests. */
export function clearVisibilityPatternCache(): void {
    patternCache.clear();
}

/**
 * An include condition over a single viewer attribute.
 * An empty list never matches — that is how the spec expresses "always invisible".
 * A null attribute never matches, so `include-states` does not match a viewer with no state.
 */
function includesValue(list: string[], value: string | null): boolean {
    if (list.length === 0) return false;
    if (value == null) return false;
    return list.includes(value);
}

/**
 * The exclude counterpart. An empty list excludes nobody, and a null attribute is not
 * excluded — a viewer with no state set is not removed by `exclude-states`.
 */
function excludesValue(list: string[], value: string | null): boolean {
    if (value == null) return false;
    return list.includes(value);
}

function anyPatternMatches(patterns: string[], url: string): boolean {
    for (const pattern of patterns) {
        const compiled = compilePattern(pattern);
        // A pattern that will not compile is treated as non-matching, so a typo in Strapi
        // hides the item rather than widening its audience.
        if (compiled != null && compiled.test(url)) return true;
    }
    return false;
}

/** All condition keys, in the order the diagnostics page lists them. */
const CONDITION_KEYS = [
    'include-pages', 'exclude-pages',
    'include-url', 'exclude-url',
    'include-identity', 'exclude-identity',
    'include-ages', 'exclude-ages',
    'include-states', 'exclude-states',
] as const;

export type ConditionKey = typeof CONDITION_KEYS[number];

export interface ConditionOutcome {
    key: ConditionKey;
    /** Values as authored (after normalizing a single value into a list). */
    values: string[];
    /** The viewer attribute this condition was tested against. */
    actual: string | null;
    passed: boolean;
}

export interface RuleOutcome {
    index: number;
    matched: boolean;
    conditions: ConditionOutcome[];
    /** Set when the rule did not parse; it can never match. */
    error?: string;
}

export interface VisibilityExplanation {
    visible: boolean;
    /** True when the field was absent or empty — visible to everyone. */
    unrestricted: boolean;
    rules: RuleOutcome[];
}

/**
 * Read one condition off a rule as a plain string list.
 *
 * Indexing with a union of keys widens to a union of all their element types (state codes,
 * age buckets, identities), which TypeScript will not narrow back to string[]. Every
 * condition value is a list of strings at runtime — zod has already validated the members —
 * so this is the one place that says so.
 */
function conditionValues(rule: VisibilityRule, key: ConditionKey): string[] | undefined {
    return rule[key] as string[] | undefined;
}

/** The viewer attribute a condition key is tested against. */
function actualFor(key: ConditionKey, ctx: ViewerContext): string | null {
    if (key === 'include-pages' || key === 'exclude-pages') return ctx.page;
    if (key === 'include-url' || key === 'exclude-url') return ctx.url;
    if (key === 'include-identity' || key === 'exclude-identity') return ctx.identity;
    if (key === 'include-ages' || key === 'exclude-ages') return ctx.ageBucket;
    return ctx.stateCode;
}

/** Evaluate one condition in isolation, for both matching and diagnostics. */
function conditionPasses(key: ConditionKey, values: string[], ctx: ViewerContext): boolean {
    if (key === 'include-url') return anyPatternMatches(values, ctx.url);
    if (key === 'exclude-url') return !anyPatternMatches(values, ctx.url);
    const actual = actualFor(key, ctx);
    return key.startsWith('include-')
        ? includesValue(values, actual)
        : !excludesValue(values, actual);
}

/**
 * Per-condition results for one rule. Used by the CMS visibility diagnostics page so an
 * editor can see which condition rejected them rather than only that the item is hidden.
 */
function explainRule(rule: VisibilityRule, index: number, ctx: ViewerContext): RuleOutcome {
    const conditions: ConditionOutcome[] = [];
    for (const key of CONDITION_KEYS) {
        const values = conditionValues(rule, key);
        if (!values) continue;
        conditions.push({
            key,
            values,
            actual: actualFor(key, ctx),
            passed: conditionPasses(key, values, ctx),
        });
    }
    return { index, matched: conditions.every(c => c.passed), conditions };
}

/**
 * Full evaluation trace: what each rule and condition did. `visible` matches `isVisible`
 * exactly — both are driven by the same condition helpers.
 */
export function explainVisibility(rawVisibility: unknown, ctx: ViewerContext): VisibilityExplanation {
    const parsed = parseVisibility(rawVisibility);
    if (parsed.unrestricted) {
        return { visible: true, unrestricted: true, rules: [] };
    }

    const outcomes: RuleOutcome[] = parsed.rules.map((rule, index) => explainRule(rule, index, ctx));
    for (const bad of parsed.invalid) {
        outcomes.push({ index: bad.index, matched: false, conditions: [], error: bad.reason });
    }
    outcomes.sort((a, b) => a.index - b.index);

    return {
        visible: outcomes.some(outcome => outcome.matched),
        unrestricted: false,
        rules: outcomes,
    };
}

/**
 * Every condition in a rule is ANDed. Shares `conditionPasses` with `explainVisibility`, so
 * the diagnostics page can never disagree with what the app actually renders.
 */
function ruleMatches(rule: VisibilityRule, ctx: ViewerContext): boolean {
    for (const key of CONDITION_KEYS) {
        const values = conditionValues(rule, key);
        if (!values) continue;
        if (!conditionPasses(key, values, ctx)) return false;
    }
    return true;
}

function warnInvalid(parsed: ParsedVisibility, label?: string): void {
    if (parsed.invalid.length === 0) return;
    const where = label ? ` on ${label}` : '';
    for (const bad of parsed.invalid) {
        console.warn(
            `CMS visibility: rule ${bad.index}${where} was not applied. ${bad.reason}. `
            + 'This rule does not match anyone, so it cannot make the item visible.',
        );
    }
}

/**
 * Decide whether an item with these rules is visible to this viewer.
 * OR across rules; an absent or empty field is visible to everyone.
 *
 * @param rawVisibility the `visibility` field straight off the CMS payload.
 * @param label optional identifier used in the invalid-rule warning (e.g. an article slug).
 */
export function isVisible(rawVisibility: unknown, ctx: ViewerContext, label?: string): boolean {
    const parsed = parseVisibility(rawVisibility);
    warnInvalid(parsed, label);

    if (parsed.unrestricted) return true;
    // Present but nothing usable parsed: fail closed rather than widen the audience.
    if (parsed.rules.length === 0) return false;

    return parsed.rules.some(rule => ruleMatches(rule, ctx));
}

/** Filter a list of CMS items by their own `visibility` field. */
export function filterVisible<T>(
    items: T[],
    ctx: ViewerContext,
    getVisibility: (item: T) => unknown,
    getLabel?: (item: T) => string,
): T[] {
    return items.filter(item => isVisible(getVisibility(item), ctx, getLabel?.(item)));
}
